/**
 * The job form's Equipment section.
 *
 * Split out of JobForm because it was 349 of its ~1,270 lines - more than
 * twice the next largest block, and the only section with real branching in
 * the markup. Everything else on the form renders one row type; this renders
 * four off a single ordered list.
 *
 * That list is formData.equipment_display_order. Each entry names an
 * equipment item, a package, a divider or a crew equipment row, and the whole
 * lot reorders together through one DndContext - which is why these cannot be
 * four separate lists. dndIdForOrderItem gives each entry the prefixed id
 * dnd-kit drags by; see useJobFormHandlers.
 *
 * The rental-date fields live here too: they belong to the Equipment card,
 * because an equipment-only rental is what they describe.
 */

import { FormSectionHeader } from './FormSectionHeader';
import {
  Card, CardContent,
} from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { DatePicker } from '../ui/date-picker';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { GripVertical, Package, Pencil, Plus, Trash2, Wrench, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { DndContext, closestCenter, DragOverlay } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import {
  SortableEquipmentItem,
  SortablePackageItem,
  SortableDividerItem,
  SortableCrewEquipmentItem,
  EquipmentMultiSelect,
  CrewEquipmentSelect,
  RateModeToggle,
  EQUIPMENT_RATE_MODES,
} from './JobFormRows';

/**
 * @param {object}   props
 * @param {object}   props.formData
 * @param {Function} props.setFormData
 * @param {Array}    props.equipment       equipment inventory
 * @param {Array}    props.equipmentKits   saved kits
 * @param {Array}    props.crewMembers     crew inventory
 * @param {boolean}  props.isAdmin         crew equipment is admin-only
 * @param {object}   props.handlers        the useJobFormHandlers result
 * @param {Array}    props.sensors         dnd-kit sensors, shared with the rest of the form
 * @param {Function} props.formatCurrency
 * @param {object}   props.regionConfig
 * @param {Function} props.calculatePackageTotal
 * @param {Function} props.onEditPackage   opens the package dialog
 */
export function JobEquipmentSection({
  formData,
  setFormData,
  equipment,
  equipmentKits,
  crewMembers,
  isAdmin,
  handlers,
  sensors,
  formatCurrency,
  regionConfig,
  calculatePackageTotal,
  onEditPackage,
}) {
  const {
    kitSelectKey,
    activeEquipmentDragId,
    dndIdForOrderItem,
    getPackageForEquipment,
    addEquipment, addKitEquipment, updateEquipment, removeEquipment,
    addCrewEquipmentFromSelect, updateCrewEquipment, removeCrewEquipment,
    addDivider, updateDivider, removeDivider,
    removePackage,
    handleCombinedDragStart, handleCombinedDragEnd,
  } = handlers;

  const openPackageDialog = onEditPackage;

  // The "Add from ..." row had a hardcoded three columns, so a user without
  // kits or without crew access got the same layout with an empty slot in it.
  // Track what actually renders and give the grid that many columns.
  const showKitSelect = equipmentKits.length > 0;
  const showCrewSelect = !formData.equipment_rental_only && isAdmin;
  const addFromCount = 1 + (showKitSelect ? 1 : 0) + (showCrewSelect ? 1 : 0);
  // Floored at two: a lone inventory select stretched across the whole card
  // reads worse than one sitting in half of it. Written as whole class names
  // so Tailwind's scanner can see them.
  const addFromColumnsClass = addFromCount >= 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2';

  return (
    <Card>
      <FormSectionHeader
        icon={Wrench}
        title="Equipment"
        action={formData.equipment.length >= 2 ? { label: 'Create Package', icon: Package, onClick: () => openPackageDialog() } : undefined}
      />
      <CardContent className="space-y-4 max-sm:px-4 max-sm:pb-4">
        <div className={cn('grid grid-cols-1 gap-4 items-end', addFromColumnsClass)}>
          <div className="space-y-2">
            <Label className="block truncate" title="Add from Inventory">Add from Inventory</Label>
            <EquipmentMultiSelect
              equipment={equipment}
              selectedIds={formData.equipment.map(e => e.inventory_id)}
              onSelect={addEquipment}
              formatCurrency={formatCurrency}
            />
          </div>

          {/* Add Kit Dropdown */}
          {showKitSelect && (
            <div className="space-y-2">
              <Label className="block truncate" title="Add from Kit">Add from Kit</Label>
              <Select key={kitSelectKey} onValueChange={addKitEquipment}>
                <SelectTrigger data-testid="add-kit-select">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {equipmentKits.map((kit) => (
                    <SelectItem key={kit.id} value={kit.id}>
                      <div className="flex items-center gap-2">
                        <span>{kit.name}</span>
                        <span className="text-xs text-muted-foreground">
                          ({kit.equipment_items?.length || 0} items)
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Add Crew / Supplier Equipment Dropdown */}
          {showCrewSelect && (
            <div className="space-y-2">
              <Label className="block truncate" title="Add Crew / Supplier Equipment">Add Crew / Supplier Equipment</Label>
              <CrewEquipmentSelect
                crewMembers={crewMembers}
                onSelect={addCrewEquipmentFromSelect}
              />
            </div>
          )}
        </div>

        {/* Combined Equipment & Packages with unified drag & drop */}
        {formData.equipment_display_order.length > 0 && (
          <DndContext 
            sensors={sensors} 
            collisionDetection={closestCenter} 
            onDragStart={handleCombinedDragStart}
            onDragEnd={handleCombinedDragEnd}
          >
            <SortableContext 
              items={formData.equipment_display_order.map(dndIdForOrderItem)} 
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2">
                {formData.equipment_display_order.map((orderItem) => {
                  if (orderItem.type === 'package') {
                    const pkg = formData.equipment_packages.find(p => p.id === orderItem.id);
                    if (!pkg) return null;
                    return (
                      <SortablePackageItem
                        key={`pkg-${pkg.id}`}
                        id={`pkg-${pkg.id}`}
                        pkg={pkg}
                        equipment={formData.equipment}
                        onEdit={openPackageDialog}
                        onRemove={removePackage}
                        calculatePackageTotal={calculatePackageTotal}
                        formatCurrency={formatCurrency}
                      />
                    );
                  } else if (orderItem.type === 'divider') {
                    return (
                      <SortableDividerItem
                        key={`div-${orderItem.id}`}
                        id={`div-${orderItem.id}`}
                        divider={orderItem}
                        onUpdate={updateDivider}
                        onRemove={removeDivider}
                      />
                    );
                  } else if (orderItem.type === 'crew_equipment') {
                    const ceIndex = formData.crew_equipment.findIndex(c => c.id === orderItem.id);
                    if (ceIndex === -1) return null;
                    const ceItem = formData.crew_equipment[ceIndex];
                    return (
                      <SortableCrewEquipmentItem
                        key={`crew-${ceItem.id}`}
                        id={`crew-${ceItem.id}`}
                        item={ceItem}
                        index={ceIndex}
                        onUpdate={updateCrewEquipment}
                        onRemove={removeCrewEquipment}
                        formatCurrency={formatCurrency}
                      />
                    );
                  } else {
                    const item = formData.equipment.find(e => e.id === orderItem.id);
                    if (!item) return null;
                    // Skip if this equipment is inside a package
                    if (getPackageForEquipment(item.inventory_id)) return null;
                    const index = formData.equipment.findIndex(e => e.id === item.id);
                    return (
                      <SortableEquipmentItem
                        key={`eq-${item.id}`}
                        id={`eq-${item.id}`}
                        item={item}
                        index={index}
                        onUpdate={updateEquipment}
                        onRemove={removeEquipment}
                        formatCurrency={formatCurrency}
                      />
                    );
                  }
                })}
              </div>
            </SortableContext>
            <DragOverlay>
              {activeEquipmentDragId ? (
                (() => {
                  // Check if it's a package or equipment
                  if (activeEquipmentDragId.startsWith('pkg-')) {
                    const pkgId = activeEquipmentDragId.replace('pkg-', '');
                    const pkg = formData.equipment_packages.find(p => p.id === pkgId);
                    if (pkg) {
                      const { subtotal, discount, total } = calculatePackageTotal(pkg);
                      const itemsInPackage = formData.equipment.filter(item => pkg.equipment_ids.includes(item.inventory_id));
                      const hasDiscount = pkg.discount_value > 0;
                      const getDiscountLabel = () => {
                        if (pkg.discount_type === 'percentage') return `${pkg.discount_value}% off`;
                        if (pkg.discount_type === 'budget') return `Budget: ${formatCurrency(pkg.discount_value)}`;
                        return `${formatCurrency(pkg.discount_value)} off`;
                      };
                      return (
                        <div className="p-3 bg-mist rounded-lg shadow-modal">
                          <div className="flex items-start gap-3">
                            <GripVertical className="w-4 h-4 mt-0.5 text-muted-foreground" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <Package className="w-4 h-4 text-primary" />
                                  <span className="font-medium">{pkg.name}</span>
                                  {hasDiscount && (
                                    <span className="text-xs text-muted-foreground px-2 py-0.5 bg-primary/10 rounded">
                                      {getDiscountLabel()}
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-1">
                                  <Button type="button" variant="ghost" size="icon" className="h-7 w-7">
                                    <Pencil className="w-4 h-4 text-muted-foreground" />
                                  </Button>
                                  <Button type="button" variant="ghost" size="icon" className="h-7 w-7">
                                    <X className="w-4 h-4 text-muted-foreground" />
                                  </Button>
                                </div>
                              </div>
                              <div className="space-y-1 mb-2 pl-1">
                                {itemsInPackage.map((item) => (
                                  <div key={item.id} className="flex items-center justify-between text-sm">
                                    <span className="text-muted-foreground">{item.name}</span>
                                    <span className="font-mono text-muted-foreground">
                                      {formatCurrency((item.rate_override || item.daily_rate) * item.scheduled_days * item.quantity)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                              <div className="border-t pt-2 space-y-1">
                                {hasDiscount ? (
                                  <>
                                    <div className="flex justify-between text-sm">
                                      <span className="text-muted-foreground">Subtotal</span>
                                      <span className="font-mono line-through text-muted-foreground">{formatCurrency(subtotal)}</span>
                                    </div>
                                    <div className="flex justify-between text-sm text-primary">
                                      <span>Package Discount</span>
                                      <span className="font-mono">-{formatCurrency(discount)}</span>
                                    </div>
                                  </>
                                ) : null}
                                <div className="flex justify-between font-medium">
                                  <span>Package Total</span>
                                  <span className="font-mono">{formatCurrency(total)}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    }
                  } else if (activeEquipmentDragId.startsWith('eq-')) {
                    const eqId = activeEquipmentDragId.replace('eq-', '');
                    const item = formData.equipment.find(e => e.id === eqId);
                    if (item) {
                      const rate = item.rate_override !== null && item.rate_override !== undefined
                        ? item.rate_override
                        : (item.rate_mode === 'week' ? (item.weekly_rate || 0) : (item.daily_rate || 0));
                      const rateSuffix = item.rate_mode === 'week' ? '/wk' : '/day';
                      return (
                        <div className="p-3 bg-mist rounded-lg shadow-modal">
                          <div className="flex items-start gap-3">
                            <GripVertical className="w-4 h-4 mt-0.5 text-muted-foreground" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium truncate">{item.name}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {formatCurrency(rate)}{rateSuffix}
                                  </p>
                                </div>
                                <div className="hidden sm:flex items-start gap-2">
                                  <div className="flex gap-2 -mt-0.5">
                                    <div className="w-14">
                                      <Label className="text-[10px] text-muted-foreground leading-none block h-[14px]">Qty</Label>
                                      <Input type="number" value={item.quantity} readOnly className="h-7 text-sm no-spinner mt-0.5" />
                                    </div>
                                    <div className="w-16">
                                      <div className="flex items-center h-[14px]">
                                        <RateModeToggle readOnly value={item.rate_mode === 'week' ? 'week' : 'day'} options={EQUIPMENT_RATE_MODES} />
                                      </div>
                                      <Input type="number" value={item.scheduled_days} readOnly className="h-7 text-sm no-spinner mt-0.5" />
                                    </div>
                                    <div className="w-16">
                                      <Label className="text-[10px] text-muted-foreground leading-none block h-[14px]">Rate</Label>
                                      <Input type="number" value={rate || ''} readOnly className="h-7 text-sm no-spinner mt-0.5" />
                                    </div>
                                    <div className="w-20">
                                      <Label className="text-[10px] text-muted-foreground leading-none block h-[14px]">Total</Label>
                                      <div className="h-7 flex items-center font-mono text-sm mt-0.5">
                                        {formatCurrency(rate * item.scheduled_days * item.quantity)}
                                      </div>
                                    </div>
                                  </div>
                                  <Button type="button" variant="ghost" size="icon" className="h-7 w-7 -mt-1">
                                    <Trash2 className="w-4 h-4 text-muted-foreground" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    }
                  } else if (activeEquipmentDragId.startsWith('div-')) {
                    const divId = activeEquipmentDragId.replace('div-', '');
                    const divider = formData.equipment_display_order.find(o => o.type === 'divider' && o.id === divId);
                    if (divider) {
                      return (
                        <div className="flex items-center gap-2 py-1 bg-background rounded-pill shadow-modal px-2">
                          <GripVertical className="w-4 h-4 text-muted-foreground" />
                          <div className="h-px flex-1 bg-border" />
                          <span className="text-[15px] font-medium px-2">{divider.label || 'Section label'}</span>
                          <div className="h-px flex-1 bg-border" />
                        </div>
                      );
                    }
                  } else if (activeEquipmentDragId.startsWith('crew-')) {
                    const crewId = activeEquipmentDragId.replace('crew-', '');
                    const ce = formData.crew_equipment.find(c => c.id === crewId);
                    if (ce) {
                      return (
                        <div className="rounded-lg p-4 shadow-modal bg-mist border border-dashed border-line-strong">
                          <div className="flex items-center gap-3">
                            <GripVertical className="w-5 h-5 text-muted-foreground" />
                            <span className="font-medium">{ce.crew_name}{ce.description ? ` | ${ce.description}` : ''}</span>
                          </div>
                        </div>
                      );
                    }
                  }
                  return null;
                })()
              ) : null}
            </DragOverlay>
          </DndContext>
        )}

        {/* Add Divider Line */}
        <button
          type="button"
          onClick={addDivider}
          className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-border py-2 text-sm text-muted-foreground transition-colors hover:border-primary hover:text-primary"
          data-testid="add-divider-button"
        >
          <Plus className="w-4 h-4" /> Add Divider Line
        </button>
      </CardContent>

      {/* Equipment Rental Only Toggle */}
      <div className="px-6 pb-5 pt-3">
        <div className="flex items-center gap-3">
          <Switch
            id="rental-only-toggle"
            checked={formData.equipment_rental_only}
            onCheckedChange={(checked) => setFormData(prev => ({ ...prev, equipment_rental_only: checked }))}
          />
          <Label htmlFor="rental-only-toggle" className="text-sm font-medium cursor-pointer">
            Equipment Rental Only
          </Label>
        </div>
        {formData.equipment_rental_only && (
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Rental Start Date</Label>
              <DatePicker
                value={formData.rental_start_date || ''}
                onChange={(value) => setFormData(prev => ({ ...prev, rental_start_date: value }))}
                placeholder="Select date"
                showIcon={false}
                region={regionConfig.code}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Rental End Date</Label>
              <DatePicker
                value={formData.rental_end_date || ''}
                onChange={(value) => setFormData(prev => ({ ...prev, rental_end_date: value }))}
                placeholder="Select date"
                showIcon={false}
                region={regionConfig.code}
              />
            </div>
          </div>
        )}
        {formData.equipment_rental_only && formData.rental_start_date && formData.rental_end_date && formData.rental_start_date > formData.rental_end_date && (
          <p className="text-xs text-destructive mt-2">End date must be on or after start date.</p>
        )}
      </div>
    </Card>
  );
}
